import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { useHistory } from 'react-router-dom'
import { LucideIcon } from 'utils/lucideIcon'
import { SearchFooter } from './components/searchFooter'
import { SearchInput } from './components/searchInput'
import { SearchResultRow } from './components/searchResultRow'
import { SearchSkeletonRows } from './components/searchSkeletonRows'
import { useGlobalSearch } from './hooks/useGlobalSearch'
import { useGlobalSearchOverlay } from './hooks/useGlobalSearchOverlay'
import { usePageResults } from './hooks/usePageResults'
import { useRecentResults } from './hooks/useRecentResults'
import { useReservationReferenceFallback } from './hooks/useReservationReferenceFallback'
import { useScopedView } from './hooks/useScopedView'
import { useSearchKeyboardNav } from './hooks/useSearchKeyboardNav'
import { useSearchSession, useSearchSessionSteps } from './hooks/useSearchSessionTracking'
import { PageResult } from './pageDirectory'
import { RecentResult } from './recentSearches'
import {
    MIN_QUERY_LENGTH,
    resolveDeeplink,
    resultSubtitle,
    SEARCH_TYPE_META,
    SearchResult,
    SearchTypeKey,
} from './schema'
import { ActivationVia } from './searchSession'
import { suggestScopePhrases } from './scopedSearch'
import { useTranslation } from 'translations'
import './styles.scss'

export function GlobalSearchOverlay(): JSX.Element | null {
    const history = useHistory()
    const { t } = useTranslation()
    // Rotating input hints: every one is a query the user can type verbatim.
    // Verified with design (2026-07-22); teaches the scope grammar by example.
    const searchHints = [
        t('globalSearch.overlay.hints.email'),
        t('globalSearch.overlay.hints.orderNumber'),
        t('globalSearch.overlay.hints.ordersCanceled'),
        t('globalSearch.overlay.hints.membersOverdue'),
        t('globalSearch.overlay.hints.groupBookings'),
        t('globalSearch.overlay.hints.dailySchedule'),
        t('globalSearch.overlay.hints.productName'),
    ]
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [query, setQuery] = React.useState('')
    // One usage event per open → close/result-opened cycle (searchSession.ts).
    const session = useSearchSession()

    // First Escape clears a typed query; the second closes the overlay.
    const queryRef = React.useRef(query)
    queryRef.current = query
    const onEscape = React.useCallback(() => {
        if (queryRef.current) {
            setQuery('')
            session.recordCleared()
            return true
        }
        return false
    }, [session])
    const { isOpen, close } = useGlobalSearchOverlay(
        (via) => {
            setQuery('')
            session.onOpen(via)
        },
        onEscape,
        session.onClose,
    )
    const {
        accountSlug,
        hasQuery,
        debouncedQuery,
        errorCode,
        loading,
        fetching,
        orderedGroups,
        flatRows,
        filtersDropped,
        emailFallback,
        emailSearchExhausted,
        errorMessage,
        retry,
    } = useGlobalSearch({
        query,
        enabled: isOpen,
    })
    const { recentResults, recentQueries, recordResult, recordView, recordQuery } = useRecentResults(
        accountSlug,
        isOpen,
    )
    // Matching Control Panel pages, shown alongside record results.
    const directoryResults = usePageResults(query, isOpen)
    // Scope grammar row ("orders canceled last month" → filtered orders view),
    // ranked first and deduplicated against equivalent directory quick scopes.
    const scopedView = useScopedView(query, orderedGroups, isOpen)
    // Way out for reservation references the federated search doesn't index.
    const reservationFallback = useReservationReferenceFallback(query, orderedGroups, loading, isOpen)
    // Deterministic completion chips from the grammar's own tables.
    const scopeSuggestions = React.useMemo(() => suggestScopePhrases(query), [query])
    const pageResults = React.useMemo(() => {
        // The badge param never differs semantically — ignore it when deduping
        // the scoped row against equivalent directory quick scopes.
        const stripViaSearch = (path: string) => path.replace(/[?&]viaSearch=[^&]*$/, '')
        const base = scopedView
            ? [
                  scopedView,
                  ...directoryResults.filter((page) => stripViaSearch(page.path) !== stripViaSearch(scopedView.path)),
              ]
            : directoryResults
        return reservationFallback ? [...base, reservationFallback] : base
    }, [scopedView, directoryResults, reservationFallback])

    useSearchSessionSteps(session.sessionRef, {
        isOpen,
        accountSlug,
        hasQuery,
        debouncedQuery,
        loading,
        fetching,
        orderedGroups,
        pageResults,
        scopedView,
        reservationFallback,
        emailFallback,
        filtersDropped,
        errorCode,
    })
    React.useEffect(() => {
        session.setLiveQuery(query)
    }, [session, query])

    // Arrow keys navigate whichever list is on screen: pages + results, or
    // recent queries + recently opened.
    const navigableCount = hasQuery ? pageResults.length + flatRows.length : recentQueries.length + recentResults.length
    const { activeIndex, setActiveIndex, rowRefs, onArrowKeyDown } = useSearchKeyboardNav(navigableCount)

    React.useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus()
        }
    }, [isOpen])

    // Reset the highlight whenever the visible list changes.
    React.useEffect(() => {
        setActiveIndex(0)
    }, [orderedGroups, pageResults, hasQuery, setActiveIndex])

    // Container transform: the panel stays anchored where the trigger sits and
    // grows rightward and downward out of it, so the button — which already
    // looks like a search input — simply becomes the palette in place. The CSS
    // rise-to-center stays as the fallback when there is no trigger on screen,
    // and reduced motion keeps its plain fade.
    const shellRef = React.useRef<HTMLDivElement>(null)
    React.useLayoutEffect(() => {
        if (!isOpen) {
            return
        }
        const shell = shellRef.current
        const trigger = document.querySelector<HTMLElement>('.global-search-trigger')
        const panel = shell?.querySelector<HTMLElement>('.global-search-panel')
        if (!shell || !trigger || !panel || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }
        const from = trigger.getBoundingClientRect()
        if (!from.width) {
            return
        }
        // The shell keeps the trigger's vertical spot but settles horizontally
        // centered; the morph starts at the trigger's exact box and glides there
        // while it grows.
        shell.style.animation = 'none'
        shell.style.position = 'fixed'
        shell.style.left = `${Math.max(16, (window.innerWidth - shell.offsetWidth) / 2)}px`
        shell.style.top = `${from.top}px`
        // The panel's natural size at full width, measured before first paint.
        const to = panel.getBoundingClientRect()
        if (!to.width || !to.height) {
            return
        }
        const morphTiming: KeyframeAnimationOptions = { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        // A top-left-anchored translate-and-scale grows the box rightward and
        // downward while it slides to center, and stays on the compositor (a
        // width/height animation would reflow every frame and freeze entirely
        // in background tabs).
        shell.style.transformOrigin = '0 0'
        shell.animate(
            [
                {
                    transform: `translateX(${from.left - to.left}px) scale(${from.width / to.width}, ${
                        from.height / to.height
                    })`,
                },
                { transform: 'translateX(0) scale(1, 1)' },
            ],
            morphTiming,
        )
        // The white container carries the shape change; its content only fades
        // in once the shape is mostly there, the way container transforms read.
        Array.from(panel.children).forEach((child) =>
            (child as HTMLElement).animate([{ opacity: 0 }, { opacity: 0, offset: 0.35 }, { opacity: 1 }], morphTiming),
        )
    }, [isOpen])

    // Track the natural height of the panel body so height changes (results
    // loading in) animate instead of jumping.
    const bodyInnerRef = React.useRef<HTMLDivElement>(null)
    const [bodyHeight, setBodyHeight] = React.useState<number | undefined>(undefined)
    React.useEffect(() => {
        if (!isOpen) {
            setBodyHeight(undefined)
            return
        }
        const element = bodyInnerRef.current
        if (!element) {
            return
        }
        // offsetHeight, not getBoundingClientRect: the entry morph scales the
        // whole shell, and a rect measured mid-morph would record the shrunken
        // height as the body's size.
        const observer = new ResizeObserver(() => setBodyHeight(element.offsetHeight))
        observer.observe(element)
        return () => observer.disconnect()
    }, [isOpen])

    if (!isOpen) {
        return null
    }

    const totalResults = flatRows.length

    // A query that led anywhere is worth re-running later.
    const recordActedQuery = () => {
        if (query.trim().length >= MIN_QUERY_LENGTH) {
            recordQuery(query)
        }
    }

    // Where in the keyboard-nav list the activated row sat, and how it was
    // activated — the session event needs both to tell "found it at the top"
    // from "scrolled past four rows".
    type Activation = { position: number; via: ActivationVia }

    const openResult = (result: SearchResult, activation: Activation) => {
        if (!accountSlug) {
            return
        }
        const deeplink = resolveDeeplink(result, accountSlug)
        recordActedQuery()
        recordResult(result, deeplink)
        session.recordOutcome({ kind: 'result', type: result.type, id: result.id, ...activation })
        close()
        history.push(deeplink)
    }

    const applyRecentQuery = (recentQuery: string) => {
        session.recordQueryVia('recent')
        setQuery(recentQuery)
        inputRef.current?.focus()
    }

    const openRecentResult = (recent: RecentResult, activation: Activation) => {
        session.recordOutcome({ kind: 'recent_result', type: recent.type, ...activation })
        close()
        history.push(recent.deeplink)
    }

    const openViewAll = (path: string, type: SearchTypeKey) => {
        recordActedQuery()
        session.recordOutcome({ kind: 'view_all', type })
        close()
        history.push(path)
    }

    const openPage = (page: PageResult, activation: Activation) => {
        recordActedQuery()
        recordView(page)
        session.recordOutcome({ kind: 'page', path: page.path, scoped: page === scopedView, ...activation })
        close()
        history.push(page.path)
    }

    const applySuggestion = (phrase: string) => {
        session.recordQueryVia('suggestion')
        const base = scopeSuggestions.partial
            ? query.slice(0, query.length - scopeSuggestions.partial.length)
            : query.endsWith(' ') || query === ''
            ? query
            : `${query} `
        setQuery(`${base}${phrase} `)
        inputRef.current?.focus()
    }

    const onInputKeyDown = (event: React.KeyboardEvent) => {
        if (onArrowKeyDown(event)) {
            return
        }
        if (event.key === 'Enter') {
            const activation: Activation = { position: activeIndex, via: 'enter' }
            if (hasQuery && pageResults[activeIndex]) {
                openPage(pageResults[activeIndex], activation)
            } else if (hasQuery && flatRows[activeIndex - pageResults.length]) {
                openResult(flatRows[activeIndex - pageResults.length].result, activation)
            } else if (!hasQuery && recentQueries[activeIndex]) {
                applyRecentQuery(recentQueries[activeIndex])
            } else if (!hasQuery && recentResults[activeIndex - recentQueries.length]) {
                openRecentResult(recentResults[activeIndex - recentQueries.length], activation)
            }
        }
    }

    // Keep Tab cycling inside the dialog while it is open.
    const onPanelKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Tab') {
            return
        }
        const focusables = event.currentTarget.querySelectorAll<HTMLElement>('button, input')
        if (!focusables.length) {
            return
        }
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    // Record rows are indexed after the page rows in the keyboard-nav list.
    let rowIndex = pageResults.length - 1

    return ReactDOM.createPortal(
        <div className="global-search-backdrop" onMouseDown={() => close('backdrop')}>
            <div className="global-search-shell" ref={shellRef}>
                <div className="global-search-underglow" aria-hidden="true" />
                <div
                    className={`global-search-panel${hasQuery ? ' is-typing' : ''}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('globalSearch.overlay.ariaLabel')}
                    onMouseDown={(event) => event.stopPropagation()}
                    onKeyDown={onPanelKeyDown}
                >
                    <SearchInput
                        inputRef={inputRef}
                        value={query}
                        hints={searchHints}
                        listboxId={
                            hasQuery || recentResults.length > 0 || recentQueries.length > 0
                                ? 'global-search-results'
                                : undefined
                        }
                        activeDescendantId={
                            navigableCount > 0 && activeIndex < navigableCount
                                ? `global-search-option-${activeIndex}`
                                : undefined
                        }
                        onChange={setQuery}
                        onKeyDown={onInputKeyDown}
                    />
                    <div className="global-search-body" style={{ height: bodyHeight }}>
                        <div className="global-search-body-inner" ref={bodyInnerRef}>
                            {hasQuery && scopeSuggestions.phrases.length > 0 && (
                                <div
                                    className="global-search-suggestions"
                                    aria-label={t('globalSearch.overlay.addFilter')}
                                >
                                    {scopeSuggestions.phrases.map((phrase) => (
                                        <button
                                            key={phrase}
                                            type="button"
                                            className="global-search-suggestion-chip"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => applySuggestion(phrase)}
                                        >
                                            {phrase}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {/* Hidden entirely while idle and empty, so the collapsed
                                search box keeps equal whitespace on all sides. */}
                            {/* Status notes live OUTSIDE the listbox (invalid as listbox
                                children) and announce politely to screen readers. */}
                            {filtersDropped && (
                                <div className="global-search-filters-note" role="status">
                                    {t('globalSearch.overlay.filtersDropped')}
                                </div>
                            )}
                            {emailFallback && (
                                <div className="global-search-fallback-note" role="status">
                                    {t('globalSearch.overlay.emailFallback', {
                                        query: emailFallback.query,
                                        localPart: emailFallback.localPart,
                                    })}
                                </div>
                            )}
                            {(hasQuery || recentResults.length > 0 || recentQueries.length > 0) && (
                                <div
                                    id="global-search-results"
                                    className="global-search-results"
                                    role="listbox"
                                    aria-label={t('globalSearch.overlay.resultsAriaLabel')}
                                >
                                    {!hasQuery && recentQueries.length > 0 && (
                                        <>
                                            <div className="global-search-section-label" aria-hidden="true">
                                                {t('globalSearch.overlay.recentlySearched')}
                                            </div>
                                            {recentQueries.map((recentQuery, index) => (
                                                <SearchResultRow
                                                    key={recentQuery}
                                                    id={`global-search-option-${index}`}
                                                    rowRef={(element) => (rowRefs.current[index] = element)}
                                                    iconName="Search"
                                                    title={recentQuery}
                                                    hint={t('globalSearch.overlay.searchAgain')}
                                                    active={index === activeIndex}
                                                    onClick={() => applyRecentQuery(recentQuery)}
                                                    onMouseEnter={() => setActiveIndex(index)}
                                                />
                                            ))}
                                        </>
                                    )}
                                    {!hasQuery && recentResults.length > 0 && (
                                        <>
                                            <div className="global-search-section-label" aria-hidden="true">
                                                {t('globalSearch.overlay.recentlyOpened')}
                                            </div>
                                            {recentResults.map((recent, offsetIndex) => {
                                                const index = offsetIndex + recentQueries.length
                                                return (
                                                    <SearchResultRow
                                                        key={recent.deeplink}
                                                        id={`global-search-option-${index}`}
                                                        rowRef={(element) => (rowRefs.current[index] = element)}
                                                        iconName="History"
                                                        title={recent.title}
                                                        subtitle={recent.subtitle}
                                                        hint={
                                                            recent.type === 'view'
                                                                ? recent.deeplink.includes('?')
                                                                    ? t('globalSearch.overlay.filteredView')
                                                                    : t('globalSearch.overlay.page')
                                                                : SEARCH_TYPE_META.find(
                                                                      (meta) => meta.key === recent.type,
                                                                  )?.label
                                                        }
                                                        active={index === activeIndex}
                                                        onClick={() =>
                                                            openRecentResult(recent, { position: index, via: 'click' })
                                                        }
                                                        onMouseEnter={() => setActiveIndex(index)}
                                                    />
                                                )
                                            })}
                                        </>
                                    )}
                                    {hasQuery && pageResults.length > 0 && (
                                        <>
                                            <div className="global-search-section-label" aria-hidden="true">
                                                {t('globalSearch.overlay.pages')}
                                            </div>
                                            {pageResults.map((page, index) => (
                                                <SearchResultRow
                                                    key={page.path}
                                                    id={`global-search-option-${index}`}
                                                    rowRef={(element) => (rowRefs.current[index] = element)}
                                                    iconName={page.iconName}
                                                    title={page.title}
                                                    subtitle={page.subtitle}
                                                    hint={
                                                        page.hint ??
                                                        (page.path.includes('?')
                                                            ? t('globalSearch.overlay.filteredView')
                                                            : t('globalSearch.overlay.page'))
                                                    }
                                                    active={index === activeIndex}
                                                    onClick={() => openPage(page, { position: index, via: 'click' })}
                                                    onMouseEnter={() => setActiveIndex(index)}
                                                />
                                            ))}
                                        </>
                                    )}
                                    {hasQuery &&
                                        !errorMessage &&
                                        orderedGroups.map(({ meta, group }) => (
                                            <div key={meta.key} role="group" aria-label={meta.label}>
                                                <div className="global-search-section-label">
                                                    {meta.label}
                                                    <span className="global-search-section-count">
                                                        {t('globalSearch.overlay.resultCount', { count: group.total })}
                                                    </span>
                                                    {group.partial && (
                                                        <span
                                                            className="global-search-partial-badge"
                                                            title={t('globalSearch.overlay.partialTitle', {
                                                                label: meta.label.toLowerCase(),
                                                            })}
                                                        >
                                                            {t('globalSearch.overlay.incomplete')}
                                                        </span>
                                                    )}
                                                    {meta.viewAllPath &&
                                                        group.total > group.results.length &&
                                                        accountSlug && (
                                                            <button
                                                                type="button"
                                                                className="global-search-view-all"
                                                                onClick={() =>
                                                                    openViewAll(
                                                                        meta.viewAllPath!(accountSlug, query.trim()),
                                                                        meta.key,
                                                                    )
                                                                }
                                                            >
                                                                {t('globalSearch.overlay.viewAll')}
                                                                <LucideIcon iconName="ArrowRight" size={11} />
                                                            </button>
                                                        )}
                                                </div>
                                                {group.results.map((result) => {
                                                    rowIndex += 1
                                                    const index = rowIndex
                                                    return (
                                                        <SearchResultRow
                                                            key={`${result.type}-${result.id}`}
                                                            id={`global-search-option-${index}`}
                                                            rowRef={(element) => (rowRefs.current[index] = element)}
                                                            iconName={meta.iconName}
                                                            title={result.title}
                                                            subtitle={resultSubtitle(result)}
                                                            active={index === activeIndex}
                                                            onClick={() =>
                                                                openResult(result, { position: index, via: 'click' })
                                                            }
                                                            onMouseEnter={() => setActiveIndex(index)}
                                                        />
                                                    )
                                                })}
                                            </div>
                                        ))}
                                </div>
                            )}
                            {/* Loading/error/empty states also live outside the listbox;
                                the messages announce via role="status". */}
                            {/* Results on screen can be stale (debounce window, refetch):
                                a trailing skeleton row signals the search is still going. */}
                            {hasQuery && loading && totalResults > 0 && !errorMessage && (
                                <SearchSkeletonRows label={t('globalSearch.overlay.stillSearching')} rowCount={1} />
                            )}
                            {hasQuery && loading && totalResults === 0 && !errorMessage && <SearchSkeletonRows />}
                            {hasQuery && !loading && errorMessage && (
                                <div className="global-search-empty">
                                    <LucideIcon iconName="SearchX" size={20} />
                                    <span role="status">{errorMessage}</span>
                                    <button
                                        type="button"
                                        className="global-search-retry"
                                        onClick={() => {
                                            session.recordRetry()
                                            retry()
                                        }}
                                    >
                                        {t('globalSearch.overlay.tryAgain')}
                                    </button>
                                </div>
                            )}
                            {hasQuery && !loading && !errorMessage && totalResults === 0 && pageResults.length === 0 && (
                                <div className="global-search-empty" role="status">
                                    <LucideIcon
                                        iconName={emailSearchExhausted ? 'MailQuestion' : 'SearchX'}
                                        size={20}
                                    />
                                    {emailSearchExhausted ? (
                                        <>
                                            <span>{t('globalSearch.overlay.noEmailMatch')}</span>
                                            <span className="global-search-empty-hint">
                                                {t('globalSearch.overlay.noEmailMatchHint')}
                                            </span>
                                        </>
                                    ) : (
                                        <span>{t('globalSearch.overlay.noResults', { query })}</span>
                                    )}
                                </div>
                            )}
                            {hasQuery && (
                                <SearchFooter
                                    loading={loading}
                                    hasQuery={hasQuery}
                                    totalResults={pageResults.length + totalResults}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="global-search-shine" aria-hidden="true">
                    <div className="global-search-shine-layer is-white" />
                    <div className="global-search-shine-layer is-coral" />
                </div>
            </div>
        </div>,
        document.body,
    )
}
